<template>
  <FlashNotifications ref="flash" />

  <div class="relative overflow-hidden flex flex-col h-app-screen pb-14 md:pb-0 bg-gray-50 dark:bg-[#0d1117]">
    <!-- One header: title, totals, refresh and the filters, as on Job Listings -->
    <header class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 shadow-[0_1px_2px_rgba(16,24,40,0.04)] relative z-20">
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 sm:px-6 pt-2.5 pb-2">
        <h1 class="text-lg font-bold tracking-tight text-gray-900 dark:text-white leading-none">Companies</h1>

        <div class="ml-auto flex items-center gap-2">
          <HelpLink path="job-tracker/fetch-company-details" label="Guide" class="hidden sm:inline-flex" />
          <div v-if="store.loaded && store.counts.all > 0" class="hidden sm:flex items-center gap-2">
            <span class="inline-flex items-center gap-1.5 h-7 px-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              <span><span class="font-bold text-gray-900 dark:text-white tabular-nums">{{ store.counts.fetched }}</span> fetched</span>
            </span>
            <span class="inline-flex items-center gap-1.5 h-7 px-2 rounded-lg border text-xs"
              :class="store.counts.pending > 0
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-900/40 text-amber-700 dark:text-amber-400'
                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'">
              <span><span class="font-bold tabular-nums">{{ store.counts.pending }}</span> waiting</span>
            </span>
          </div>

          <button type="button" @click="refresh" :disabled="store.refreshing" title="Refresh"
            class="inline-flex items-center gap-1.5 h-7 px-2 sm:px-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-[13px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
            <svg class="w-3.5 h-3.5" :class="store.refreshing ? 'animate-spin' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"/>
            </svg>
            <span class="hidden sm:inline">{{ store.refreshing ? 'Refreshing...' : 'Refresh' }}</span>
            <span class="sr-only sm:hidden">Refresh</span>
          </button>
        </div>
      </div>

      <div v-if="showFilters" class="px-4 sm:px-6 pb-2.5 flex flex-wrap items-center gap-1.5">
        <!-- Search, with suggestions from all of the user's companies (not just the page that is loaded) -->
        <div class="relative flex-1 min-w-[10rem] sm:max-w-xs">
          <SearchSuggest v-model="search" :items="suggestions" :loading="suggesting" compact wide placeholder="Search companies, industries, places" empty-title="Latest companies" />
        </div>

        <div role="group" aria-label="Which companies to show" class="inline-flex p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800">
          <button v-for="option in statusOptions" :key="option.value" type="button" @click="store.setFilters({ status: option.value })"
            :aria-pressed="store.filters.status === option.value"
            class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[13px] font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
            :class="store.filters.status === option.value
              ? 'bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'">
            {{ option.label }}
            <span class="text-[11px] font-bold tabular-nums" :class="store.filters.status === option.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">{{ option.count }}</span>
          </button>
        </div>

        <FilterDropdown label="Website" :options="WEBSITE_OPTIONS" :model-value="store.filters.website" neutral="" @update:model-value="store.setFilters({ website: $event })" />
        <FilterDropdown label="Sort" :options="SORT_OPTIONS" :model-value="store.filters.sort" neutral="recent" @update:model-value="store.setFilters({ sort: $event })" />

        <button v-if="store.isFiltered" type="button" @click="clear"
          class="inline-flex items-center gap-1 h-8 px-2 text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          Clear
        </button>
      </div>
    </header>

    <div class="flex-1 min-h-0 flex flex-col">
      <!-- First load -->
      <div v-if="!store.loaded && store.loading" class="flex-1 min-h-0 flex animate-pulse">
        <div class="w-full lg:w-[380px] lg:flex-shrink-0 lg:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] divide-y divide-gray-100 dark:divide-gray-800">
          <div v-for="n in skeletonRows" :key="n" class="flex gap-2.5 px-3 py-2">
            <div class="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
            <div class="flex-1 space-y-1.5 pt-0.5">
              <div class="h-3.5 w-3/5 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="h-3 w-4/5 rounded bg-gray-100 dark:bg-gray-800"></div>
              <div class="h-2.5 w-1/4 rounded bg-gray-100 dark:bg-gray-800"></div>
            </div>
          </div>
        </div>
        <div class="hidden lg:block flex-1 bg-white dark:bg-[#1C2128]">
          <div class="max-w-3xl mx-auto px-5 sm:px-6">
            <div class="flex gap-3.5 pt-5 pb-5">
              <div class="w-16 h-16 rounded-2xl bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
              <div class="flex-1 space-y-2.5 pt-1"><div class="h-5 w-2/3 rounded bg-gray-200 dark:bg-gray-700"></div><div class="h-3.5 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div></div>
            </div>
            <hr class="border-gray-200 dark:border-gray-700">
            <div class="py-5 space-y-3"><div v-for="n in 6" :key="n" class="h-3.5 rounded bg-gray-100 dark:bg-gray-800" :style="{ width: `${96 - n * 6}%` }"></div></div>
          </div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="store.error && !store.loaded" class="flex-1 flex flex-col items-center justify-center py-24 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
        <button @click="store.fetchCompanies" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Try again</button>
      </div>

      <!-- No companies at all -->
      <div v-else-if="store.loaded && store.counts.all === 0" class="flex-1 overflow-y-auto">
        <div class="max-w-xl mx-auto px-6 py-16 text-center">
          <div class="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mx-auto mb-5">
            <svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/></svg>
          </div>
          <h2 class="text-lg font-bold text-gray-900 dark:text-white">No companies yet</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">The companies behind your saved jobs show up here once company details are on.</p>
          <ol class="text-left space-y-3 mb-7">
            <li class="flex gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-sm p-4">
              <span class="w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
              <span class="text-sm text-gray-700 dark:text-gray-200">Turn on <span class="font-semibold">Fetch company details from LinkedIn</span> in the settings of Job Listings, under Extension.</span>
            </li>
            <li class="flex gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-sm p-4">
              <span class="w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
              <span class="text-sm text-gray-700 dark:text-gray-200">Run a search in the extension. The companies of the jobs it saves are fetched afterwards.</span>
            </li>
          </ol>
          <RouterLink to="/jobs" data-turbo-prefetch="false" class="inline-block px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm">Open Job Listings</RouterLink>
        </div>
      </div>

      <!-- The companies -->
      <div v-else class="flex-1 min-h-0 flex">
        <div class="w-full lg:w-[380px] lg:flex-shrink-0 lg:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] flex flex-col min-h-0">
          <div class="flex-shrink-0 flex items-center justify-between px-3 h-8 text-[11px] border-b border-gray-100 dark:border-gray-800" aria-live="polite">
            <span class="font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
              <template v-if="store.loading && !store.refreshing">Loading...</template>
              <template v-else>{{ store.pageInfo.total_records }} {{ store.pageInfo.total_records === 1 ? 'company' : 'companies' }}<template v-if="store.isFiltered"> found</template></template>
            </span>
            <span class="text-gray-400 dark:text-gray-500">{{ store.filters.sort === 'name' ? 'A to Z' : 'Recently fetched first' }}</span>
          </div>

          <div ref="scroller" class="flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div v-if="store.companies.length === 0" class="px-6 py-16 text-center">
              <p class="font-semibold text-gray-700 dark:text-gray-200">No companies match</p>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">Try a different search or clear the filters.</p>
              <button type="button" @click="clear" class="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">Clear filters</button>
            </div>

            <ul v-else class="divide-y divide-gray-100 dark:divide-gray-800 transition-opacity" :class="store.loading ? 'opacity-60' : ''">
              <li v-for="company in store.companies" :key="company.id" :data-company-id="company.id">
                <CompanyListItem :company="company" :selected="company.id === store.selectedId" @select="open" />
              </li>
            </ul>

            <div v-if="store.hasMore" ref="sentinel" class="py-3 text-center text-xs text-gray-400 dark:text-gray-500" aria-live="polite">
              <button v-if="store.loadMoreError" type="button" @click="store.loadMore" class="font-semibold text-blue-600 dark:text-blue-400 hover:underline">Could not load more companies. Try again</button>
              <span v-else class="inline-flex items-center gap-2">
                <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"/></svg>
                Loading more companies
              </span>
            </div>
          </div>
        </div>

        <div class="hidden lg:block flex-1 min-w-0 min-h-0 bg-white dark:bg-[#1C2128]">
          <CompanyDetail :company="store.selected" :loading="store.detailLoading" :error="store.detailError" @retry="store.select(store.selectedId)" />
        </div>
      </div>

      <!-- Detail, over the list on small screens -->
      <div v-if="mobileOpen" class="fixed inset-0 z-40 lg:hidden bg-white dark:bg-[#1C2128]">
        <CompanyDetail :company="store.selected" :loading="store.detailLoading" :error="store.detailError" show-back
          @retry="store.select(store.selectedId)" @back="mobileOpen = false" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useJobCompaniesStore } from 'stores/jobCompanies.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import FilterDropdown from './FilterDropdown.vue'
import SearchSuggest from '../shared/SearchSuggest.vue'
import CompanyListItem from './CompanyListItem.vue'
import CompanyDetail from './CompanyDetail.vue'
import HelpLink from 'controllers/vue_components/shared/HelpLink.vue'

const store = useJobCompaniesStore()
const flash = ref(null)
const mobileOpen = ref(false)
const scroller = ref(null)
const sentinel = ref(null)

const WEBSITE_OPTIONS = [
  { value: '', label: 'Any website' },
  { value: 'read', label: 'Website read' },
]
const SORT_OPTIONS = [
  { value: 'recent', label: 'Recently fetched' },
  { value: 'name', label: 'Name A to Z' },
]

const statusOptions = computed(() => [
  { value: 'all', label: 'All', count: store.counts.all },
  { value: 'fetched', label: 'Fetched', count: store.counts.fetched },
  { value: 'pending', label: 'Waiting', count: store.counts.pending },
  { value: 'blocked', label: 'Blocked', count: store.counts.blocked },
])

// Shows the filters once there is something to filter, or a filter is on and can be cleared.
const showFilters = computed(() => store.loaded && (store.counts.all > 0 || store.isFiltered))
const skeletonRows = 12

// Typing searches a moment after the last key, so each letter is not a request.
const search = ref(store.filters.q)
let typing = null
watch(search, (text) => {
  clearTimeout(typing)
  typing = setTimeout(() => { if (text !== store.filters.q) store.setFilters({ q: text }) }, 300)
  askForSuggestions(text)
})

// The suggestions come from the server (the list on screen is only one page of the companies), a moment after typing stops. Only the newest answer is used.
const suggestions = ref([])
const suggesting = ref(false)
let suggestTimer = null
let suggestRequest = 0

function askForSuggestions(text) {
  clearTimeout(suggestTimer)
  suggesting.value = true
  suggestTimer = setTimeout(async () => {
    const request = ++suggestRequest
    const found = await store.suggest(text)
    if (request !== suggestRequest) return
    suggestions.value = found
    suggesting.value = false
  }, 200)
}
askForSuggestions('') // before anything is typed the box offers the latest companies
watch(() => store.filters.q, (text) => { if (text !== search.value) search.value = text })
onBeforeUnmount(() => { clearTimeout(typing); clearTimeout(suggestTimer); suggestRequest += 1 })
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

function clear() {
  search.value = ''
  store.clearFilters()
}

// Up and down arrows move to the previous or next company in the list, like in a mail client. They are left alone while
// typing in a field, with a modifier held, or while a menu is open, so they never take a key someone else needs.
async function onKey(event) {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
  if (event.target.closest?.('input, textarea, select, [contenteditable="true"], [role="listbox"]')) return
  if (store.companies.length === 0) return

  event.preventDefault()
  const index = store.companies.findIndex((company) => company.id === store.selectedId)
  const step = event.key === 'ArrowDown' ? 1 : -1
  let target = index === -1 ? 0 : index + step

  // Past the last loaded company: bring in the next page first, if there is one.
  if (target >= store.companies.length && store.hasMore) {
    await store.loadMore()
    if (target >= store.companies.length) return
  }
  if (target < 0 || target >= store.companies.length) return

  const company = store.companies[target]
  store.select(company.id)
  await nextTick()
  scroller.value?.querySelector(`[data-company-id="${company.id}"]`)?.scrollIntoView({ block: 'nearest' })
}

const isWide = () => window.matchMedia?.('(min-width: 1024px)').matches

function open(id) {
  store.select(id)
  if (!isWide()) mobileOpen.value = true
}

// On a wide screen there is always a company open beside the list, so it never looks empty.
watch(() => store.companies, (companies) => {
  if (store.refreshing || !isWide() || companies.length === 0) return
  if (!companies.some((company) => company.id === store.selectedId)) store.select(companies[0].id)
})

async function refresh() {
  await store.refresh()
  if (store.error) flash.value?.show(`Could not refresh. ${store.error}`, 'alert')
}

// Infinite scroll, as on Job Listings: when the sentinel under the last company comes within reach, load the next page.
let observer = null

function watchSentinel() {
  observer?.disconnect()
  if (!sentinel.value || !scroller.value || typeof IntersectionObserver === 'undefined') return

  observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) store.loadMore()
  }, { root: scroller.value, rootMargin: '0px 0px 300px 0px' })
  observer.observe(sentinel.value)
}

watch(() => [store.companies.length, store.hasMore, store.loading], () => nextTick(watchSentinel), { flush: 'post' })
onBeforeUnmount(() => observer?.disconnect())

onMounted(async () => {
  await store.fetchCompanies()
  nextTick(watchSentinel)
})
</script>
